import React, { useState, useEffect, useMemo } from 'react';
import { Waypoint } from 'react-waypoint';
import { useDispatch } from 'react-redux';
import { actions as personProfileActions } from '../../../Slice/PersonProfileSlice';
import styled from 'styled-components';
import {
  primaryColor,
  maxWidth,
  primaryHeadingColor,
  retina,
  primaryFont,
  wMLight,
  wELight,
  wLight,
  maxMobile,
  maxTablet,
  maxWidth1300,
} from 'Utils/utilsStyle';
import ArrowOutwardIcon from '../../common/assets/svgIcons/ArrowActionOutward';
import SliderCircledForwardIcon from '../../common/assets/svgIcons/SliderCircledForwardIcon';
import SliderCircledBackIcon from '../../common/assets/svgIcons/SliderCircledBackIcon';
import CustomSlider from '../../common/components/CustomSlider';
import { StrategicHighlight } from '../../../Types/GlobalTypes';
import GilroyIcon from '../../company/svgIcons/GilroyIcon';
import StrategicHighlightsSkeleton from '../skeleton/StrategicHighlightsSkeleton';

const Wrapper = styled.div`
  margin-bottom: 25px;
  background-color: #fff;
  width: 100%;
  filter: drop-shadow(0 0 17px rgba(0, 0, 0, 0.04));
  border-radius: 16px;
  display: flex;
  align-items: center;
  padding: 24px 16px;

  @media all and (max-width: ${maxWidth1300}) {
    gap: 20px;
  }

  @media all and (max-width: ${maxTablet}) {
    flex-direction: column;
    align-items: stretch;
    gap: 18px;
  }

  @media all and (max-width: ${maxWidth}), ${retina} {
    padding: 16px 12px;
  }

  .strategic-highlights-info {
    width: 30%;
    min-width: 200px;
    padding: 8px 24px 8px 16px;

    @media all and (max-width: ${maxWidth}), ${retina} {
      padding: 8px 16px 8px 8px;
      min-width: 180px;
    }

    @media all and (max-width: ${maxTablet}) {
      width: 100%;
      min-width: auto;
      padding: 10px 0 8px 0;
    }
  }

  .strategic-highlights-head {
    margin-bottom: 32px;

    @media all and (max-width: ${maxWidth}), ${retina} {
      margin-bottom: 10px;
    }

    @media all and (max-width: ${maxMobile}) {
      margin-bottom: 8px;
    }
  }

  .strategic-highlights-content {
    width: 100%;
    font-family: ${primaryFont};
    color: rgb(66,75,80);
    font-size: 16px;
    font-style: normal;
    font-weight: ${wELight};
    line-height: 20px;
    letter-spacing: -0.409px;
    margin-bottom: 16px;

    @media all and (max-width: ${maxWidth}), ${retina} {
      font-size: 14px;
      margin-bottom: 12px;
    }

    @media all and (max-width: ${maxMobile}) {
      font-size: 12px;
      margin-bottom: 10px;
      width: auto;
    }
  }

  .pagination-text {
    color: #000;
    font-family: ${primaryFont};
    font-size: 14px;
    font-style: normal;
    font-weight: ${wLight};
    line-height: 20px;
    letter-spacing: -0.409px;
  }

  .slider-section {
    flex: 1;
    overflow: hidden;
    margin-right: 12px;

    @media all and (max-width: ${maxWidth}), ${retina} {
      padding-left: 0;
    }

    @media all and (max-width: ${maxTablet}) {
      width: 100%;
      margin-top: 0;
    }
  }
`;

const HighlightCard = styled.div`
  height: 168px;
  box-sizing: border-box;
  border-radius: var(--Dimenstion-Spacing-sm, 8px);
  border: 1px solid #EAEBF0;
  background: #FFF;
  padding: 20px 16px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  cursor: pointer;
  /* no hover animation: instant changes only */

  @media all and (max-width: ${maxWidth}), ${retina} {
    padding: 16px 12px;
    border-radius: 8px;
  }

  @media all and (max-width: ${maxMobile}) {
    padding: 12px 10px;
  }

  &:hover {
    border-color: ${primaryColor};
  }

  .card-title {
    color: ${primaryHeadingColor};
    font-weight: ${wMLight};
    margin: 0;
    /* scale size only (don't override family) */
    font-size: 16px;
    line-height: 20px;

    @media all and (max-width: ${maxWidth}), ${retina} {
      font-size: 14px;
      line-height: 18px;
    }

    @media all and (max-width: ${maxMobile}) {
      font-size: 13px;
      line-height: 16px;
    }
  }

  .card-footer {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
  }

  .card-icon {
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
      width: 28px;
      height: 28px;

      @media all and (max-width: ${maxWidth}), ${retina} {
        width: 24px;
        height: 24px;
      }

      @media all and (max-width: ${maxMobile}) {
        width: 20px;
        height: 20px;
      }
    }
  }

  .arrow-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0.8;

    svg {
      width: 18px;
      height: 18px;

      @media all and (max-width: ${maxWidth}), ${retina} {
        width: 16px;
        height: 16px;
      }

      @media all and (max-width: ${maxMobile}) {
        width: 14px;
        height: 14px;
      }
    }
  }

  /* On hover, change svg fill/stroke and arrow color instantly */
  &:hover {
    .card-icon path {
      fill: ${primaryColor} !important;
    }

    .arrow-icon svg,
    .arrow-icon svg * {
      fill: ${primaryColor} !important;
    }
  }
`;

const SLIDES_TO_SHOW = 4;
const parsePxValue = (rawValue: string) => Number(String(rawValue).replace(/[^\d.]/g, '')) || 0;
const BREAKPOINTS = {
  medium: parsePxValue(maxWidth1300),
  tablet: parsePxValue(maxTablet),
  mobile: parsePxValue(maxMobile),
};

type ResponsiveSlideConfig = {
  maxWidth: number;
  slidesToShow: number;
};

const RESPONSIVE_SLIDE_CONFIG: ResponsiveSlideConfig[] = [
  { maxWidth: BREAKPOINTS.mobile, slidesToShow: 2 },
  { maxWidth: BREAKPOINTS.tablet, slidesToShow: 3 },
  { maxWidth: BREAKPOINTS.medium, slidesToShow: 3 },
];

const getSlidesToShowForWidth = (width: number) => {
  const match = RESPONSIVE_SLIDE_CONFIG.find((config) => width <= config.maxWidth);
  return match ? match.slidesToShow : SLIDES_TO_SHOW;
};

const useAdaptiveSlideCount = () => {
  const getDefaultWidth = () => (typeof window === 'undefined' ? BREAKPOINTS.medium : window.innerWidth);
  const [slidesToShow, setSlidesToShow] = useState(() => getSlidesToShowForWidth(getDefaultWidth()));

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleResize = () => {
      setSlidesToShow(getSlidesToShowForWidth(window.innerWidth));
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return slidesToShow;
};

type Props = {
  strategicHighlights?: StrategicHighlight[];
  handleClick?: (prompt: string) => void;
  onGilroyClose?: () => void;
};

const StrategicHighlights = function StrategicHighlights({
  strategicHighlights = [],
  handleClick = () => { },
  onGilroyClose = () => { },
}: Props) {
  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState(1);
  const slidesToShow = useAdaptiveSlideCount();
  const sliderKey = `strategic-highlights-slider-${slidesToShow}`;

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(strategicHighlights.length / slidesToShow)),
    [slidesToShow, strategicHighlights.length],
  );

  const highlightCards = useMemo(
    () => strategicHighlights.map((highlight: StrategicHighlight) => {
      const category = (highlight as any).category;

      return (
        <div
          key={highlight.category}
          onClick={() => handleClick(highlight.prompt)}
        >
          <HighlightCard>
            <h6 className="card-title">{category}</h6>
            <div className="card-footer">
              <div className="card-icon">
                <GilroyIcon />
              </div>
              <div className="arrow-icon">
                <ArrowOutwardIcon />
              </div>
            </div>
          </HighlightCard>
        </div>
      );
    }),
    [strategicHighlights],
  );

  useEffect(() => {
    setCurrentPage((prev) => Math.min(prev, totalPages));
  }, [totalPages]);

  const handlePageChange = (page: number, sliderTotalPages?: number) => {
    const maxPages = sliderTotalPages ?? totalPages;
    setCurrentPage(Math.min(page, maxPages));
  };

  return (
    <>
      {strategicHighlights.length === 0 ? (
        <StrategicHighlightsSkeleton />
      ) : (
        <Waypoint onEnter={() => { dispatch(personProfileActions.pushCurrentSection('Strategic Highlights')); }} onLeave={() => { dispatch(personProfileActions.removeCurrentSection('Strategic Highlights')); }} bottomOffset="20%">
          <Wrapper className="strategic-highlights-wrapper">
            <div className="strategic-highlights-info">
              <div className="heading-inner-page">
                <h1 className="heading strategic-highlights-head">Strategic Highlights</h1>
              </div>
              <p className="strategic-highlights-content">
                At the core of Gilroy Agentic AI lies a commitment to
                redefining enterprise efficiency through intelligent
                automation and proactive decision-making
              </p>
              <span className="pagination-text">Page {currentPage}/{totalPages}</span>
            </div>
            <div className="slider-section">
              <CustomSlider
                key={sliderKey}
                totalItems={strategicHighlights.length}
                slidesToShow={slidesToShow}
                NextArrow={SliderCircledForwardIcon}
                PrevArrow={SliderCircledBackIcon}
                onPageChange={handlePageChange}
              >
                {highlightCards}
              </CustomSlider>
            </div>
          </Wrapper>
        </Waypoint>
      )
      }
    </>
  );
};

export default StrategicHighlights;
